import { useState } from 'react';
import EventCard from '../components/EventCard';
import OfflinePill from '../components/OfflinePill';
import Wardrobe from '../components/Wardrobe';
import IcebreakerDeck from '../components/IcebreakerDeck';
import { SkeletonEvents } from '../components/Skeleton';
import { useScheduleModel } from '../lib/schedule';
import { useIsDesktop } from '../lib/viewport';
import { deckIsOpen, useIcebreakers } from '../lib/content';
import { now } from '../lib/time';
import { MarkWhistle } from '../components/Icon';

/**
 * Artboards 1d (phone) and 1l (laptop).
 * Same components either way: on a phone the days stack under sticky headers;
 * on a laptop the planner and the newest photos move into a second column and
 * the event rows grow a time gutter. Nothing is added or removed between them.
 */
export default function Schedule() {
  const model = useScheduleModel();
  const desktop = useIsDesktop();
  /**
   * The icebreakers, which took this page's "Coming in" slot (D62, amended).
   *
   * Gated on the week the same way the header tab was, and for the same reason:
   * several cards assume the reader is standing at the wedding. The window is
   * content, so bringing it forward is a phone edit and no deploy (D12/D13).
   * The `/icebreakers` route stays open all year regardless — a link somebody
   * sent has to work.
   */
  const deck = useIcebreakers();
  const deckOpen = deckIsOpen(deck.data, now());
  /**
   * Phone only. On a laptop both are on screen at once — the schedule in the
   * main column, the deck in the rail — so there is nothing to switch between
   * and the control is not rendered.
   */
  const [tab, setTab] = useState<'schedule' | 'icebreakers'>('schedule');
  // A guest part way through the deck when the window shuts would otherwise be
  // left looking at a tab that no longer exists.
  const showing = deckOpen ? tab : 'schedule';

  if (model.loading) {
    return (
      <div className="page">
        <div className="day-head"><h4>Loading the week…</h4></div>
        <SkeletonEvents />
      </div>
    );
  }

  if (model.error && model.events.length === 0) {
    return (
      <div className="page empty">
        <MarkWhistle className="mark" size={44} />
        <h4 style={{ margin: 0 }}>The schedule didn't load</h4>
        <p>The connection dropped before we got it. It is only a small file — try once more.</p>
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>Try again</button>
      </div>
    );
  }

  const days = (
    <>
      {model.days.map((day) => (
        <section key={day.key}>
          <div className="day-head">
            <h4>{desktop ? day.longLabel : day.label}</h4>
            {day.isToday && <span className="day-note">Today</span>}
          </div>
          <div className="ev-list">
            {day.events.map((event) => {
              const state = model.stateOf(event);
              return (
                <EventCard
                  key={event.id}
                  event={event}
                  venue={model.venueOf(event.venueId)}
                  state={state}
                  at={model.at}
                  density={state === 'now' || state === 'next' ? 'hero' : 'row'}
                />
              );
            })}
          </div>
        </section>
      ))}
      <div className="tz-note">All times Khao Lak, GMT+7</div>
    </>
  );

  if (desktop) {
    return (
      <div className="cols">
        <div>
          <OfflinePill fetchedAt={model.fetchedAt} />
          {days}
        </div>
        <aside className="col-side">
          {model.wardrobe && <Wardrobe content={model.wardrobe} full />}
          {/* Where the newest photographs used to sit. The rail is ~360px, so a
              tall card is the natural shape here and needed no fight with the
              layout — which is most of why this is where the deck went. */}
          {deckOpen && (
            <>
              <div className="ice-rail-head">
                <h4>{deck.data?.title}</h4>
              </div>
              <IcebreakerDeck cards={deck.data?.cards ?? []} variant="rail" />
            </>
          )}
        </aside>
      </div>
    );
  }

  return (
    <div className="page">
      <OfflinePill fetchedAt={model.fetchedAt} />
      {/* Only while there is something to switch to. Outside the week this page
          is exactly what it was, with no control and no empty second tab. */}
      {deckOpen && (
        <div className="sched-switch">
          <div className="seg" style={{ fontSize: 12 }}>
            <label className="seg-opt">
              <input
                type="radio" name="sched-tab" checked={showing === 'schedule'}
                onChange={() => setTab('schedule')}
              />
              <span>Schedule</span>
            </label>
            <label className="seg-opt">
              <input
                type="radio" name="sched-tab" checked={showing === 'icebreakers'}
                onChange={() => setTab('icebreakers')}
              />
              <span>Icebreakers</span>
            </label>
          </div>
        </div>
      )}
      {showing === 'icebreakers' ? (
        <IcebreakerDeck cards={deck.data?.cards ?? []} />
      ) : (
        <>
          {model.wardrobe && (
            <div style={{ padding: '12px 14px 0' }}>
              <Wardrobe content={model.wardrobe} />
            </div>
          )}
          {days}
        </>
      )}
    </div>
  );
}
